/*
    The date range, as presets plus a custom range (MPN-1688).

    Presets first because they are how the question is actually asked. "Last 90 days" and
    "year to date" are the two somebody wants nine times out of ten, and making them type two
    dates for that is the kind of friction that ends with people not using the filter at all.

    The custom range applies on a button rather than on each keystroke. Two date inputs bound
    straight to the query refetch the whole portfolio halfway through entering a range, and
    the intermediate state is usually a range nobody asked for.
*/
import { useState } from "react";

import {
    PRESETS, presetRange, rangeIsBackwards, type PresetKey, type ReportScope,
} from "./holdoutReport";

export default function DateRangePicker({ scope, preset, onChange }: {
    scope: ReportScope;
    preset: PresetKey;
    onChange: (scope: ReportScope, preset: PresetKey) => void;
}) {
    const [draft, setDraft] = useState({ start: scope.start ?? "", end: scope.end ?? "" });

    const backwards = rangeIsBackwards(draft);
    const custom = preset === "custom";

    const applyCustom = () => {
        if (backwards) return;
        onChange({ ...scope, start: draft.start, end: draft.end }, "custom");
    };

    return (
        <div className="d-flex flex-wrap align-items-end gap-3">
            <div>
                {/* No visible label. The scope controls already label this group -- "Date
                    Range", with the calendar icon -- and a second label under it read as a
                    second control (Holly, QA, 6 Oct 2026). The accessible name stays, because
                    dropping the label element is a visual decision and not a semantic one. */}
                <select id="holdout-preset" aria-label="Date range"
                    className="form-select form-select-sm"
                    value={preset}
                    onChange={(event) => {
                        const next = event.target.value as PresetKey;
                        if (next === "custom") { onChange(scope, "custom"); return; }
                        const range = presetRange(next, new Date());
                        onChange({ ...scope, ...{ start: undefined, end: undefined }, ...range },
                                 next);
                    }}>
                    {PRESETS.map((option) => (
                        <option key={option.key} value={option.key}>{option.label}</option>
                    ))}
                    <option value="custom">Custom range...</option>
                </select>
            </div>

            {custom && (
                <>
                    <div>
                        <label className="form-label small mb-1" htmlFor="holdout-start">
                            From
                        </label>
                        <input id="holdout-start" type="date"
                            className="form-control form-control-sm"
                            value={draft.start}
                            onChange={(e) => setDraft({ ...draft, start: e.target.value })} />
                    </div>
                    <div>
                        <label className="form-label small mb-1" htmlFor="holdout-end">To</label>
                        <input id="holdout-end" type="date"
                            className="form-control form-control-sm"
                            value={draft.end}
                            onChange={(e) => setDraft({ ...draft, end: e.target.value })} />
                    </div>
                    <button type="button" className="btn btn-sm btn-outline-secondary"
                        disabled={backwards} onClick={applyCustom}>
                        Apply
                    </button>
                    {backwards && (
                        <div className="text-danger small align-self-center">
                            End is before start.
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
